import "./style.css";
import { useEffect, useMemo, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import BookingModal from "components/BookingModal";
import Loader from "components/Spinners/Loader";
import MapWrapper from "./MapWrapper";
import { EventsDealer } from "./EventsDealer";
import CustomMapControl from "./CustomMapControl";
import { useLoggedIn } from "stores/auth/authStore";
import { useEventStore } from "../../stores/event/useEventStore";
import { useModalStore } from "stores/modal/useModalStore";
// import NoEventIcon from "assets/icons/no-event.svg?react";
// import ArrowIcon from "assets/icons/arrow.svg?react";
// import EventModal from "../../components/Modal/Event/EventModal";

const Event = () => {
  const { user } = useLoggedIn();
  const close = useModalStore((s) => s.close);
  const openModalIndex = useEventStore((state) => state.openModalIndex);
  const selectedEvents = useEventStore((state) => state.selectedEvents);
  const eventsData = useEventStore((state) => state.eventsData);
  const isLoading = useEventStore((state) => state.isLoading);
  const error = useEventStore((state) => state.error);
  const eventTypeFilter = useEventStore((state) => state.eventTypeFilter);
  const setEventTypeFilter = useEventStore((action) => action.setEventTypeFilter);

  // Distinct event types present among enabled events — drives the filter dropdown.
  const eventTypeOptions = useMemo(() => {
    const types = new Set();
    (eventsData || []).forEach((e) => {
      if (e.visibility === "ENABLED" && e.eventType) types.add(e.eventType);
    });
    return Array.from(types).sort();
  }, [eventsData]);

  const fetchEventsData = useEventStore((action) => action.fetchEventsData);
  // const fetchDealersData = useEventStore((action) => action.fetchDealersData);
  const setOpenModalIndex = useEventStore((action) => action.setOpenModalIndex);
  const pendingBookingIndex = useEventStore((state) => state.pendingBookingIndex);
  const setPendingBookingIndex = useEventStore((action) => action.setPendingBookingIndex);

  const { pathname: eventPagePathname } = useLocation();

  useEffect(() => {
    if (user && pendingBookingIndex !== null) {
      setOpenModalIndex(pendingBookingIndex);
      setPendingBookingIndex(null);
    }
  }, [user, pendingBookingIndex, setOpenModalIndex, setPendingBookingIndex]);

  useEffect(() => {
    return () => setPendingBookingIndex(null);
  }, [setPendingBookingIndex]);

  useEffect(() => {
    fetchEventsData();
    // fetchDealersData(user); // SaddleFit authorized dealer locations — disabled while map pins are off
  }, [user, fetchEventsData, eventPagePathname]);

  useEffect(() => {
    // Update canonical tag
    const canonicalLink = document.querySelector("link[rel='canonical']");
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://saddlefit.io/event');
    }

    // Update meta tags and title
    document.title = 'Upcoming Events - SaddleFit.io';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Discover upcoming SaddleFit events near you. Join workshops and sessions to learn about saddle fitting and horse care.'
      );
    }
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 1023) {
        close();
      }
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [close]);

  // Honor ?eventId= deep link from the admin CMS / share links. Fire once per
  // distinct id so closing the modal doesn't re-open it on the next Apollo
  // store tick.
  const [searchParams] = useSearchParams();
  const handledEventIdRef = useRef(null);
  useEffect(() => {
    const wantedId = searchParams.get("eventId");
    if (!wantedId || !eventsData?.length) return;
    if (handledEventIdRef.current === wantedId) return;
    const idx = eventsData.findIndex((e) => e.id === wantedId);
    if (idx >= 0) {
      handledEventIdRef.current = wantedId;
      setOpenModalIndex(idx);
    }
  }, [searchParams, eventsData, setOpenModalIndex]);

  return (
    <>
      <EventsDealer />
      <div className="mb-[169px] relative mx-auto px-[22px] sm:px-2 xl:px-10 2xl:px-0 w-[95%] xl:max-w-[1300px]">
        <div className="max-w-[1300px] h-[623px]  flex items-center gap-4">
          <div className="!w-[100%] mt-[80px] !min-h-[623px]">
            {isLoading ? (
              <div className="flex justify-center items-center w-full">
                <Loader
                  filledColor="#2b364b"
                  className="!h-[60px] !flex !items-center !text-center !justify-center"
                />
              </div>
            ) : error ? (
              <div className="flex flex-col justify-center items-center gap-2 w-full min-h-[300px] font-[Montserrat] text-center">
                <p className="text-[#343434] text-[16px] font-bold leading-6">
                  Unable to load events right now.
                </p>
                <p className="text-[#808693] text-[14px] font-normal leading-6">
                  Please refresh the page or try again later.
                </p>
              </div>
            ) : (
              <>
                {eventTypeOptions.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 font-[Montserrat]">
                    <label htmlFor="event-type-filter" className="text-[13px] text-[#2b364b] font-semibold">
                      Filter by type:
                    </label>
                    <select
                      id="event-type-filter"
                      value={eventTypeFilter}
                      onChange={(e) => setEventTypeFilter(e.target.value)}
                      className="text-[13px] border border-[#c3c9d4] rounded-[8px] px-3 h-[36px] bg-white"
                    >
                      <option value="">All event types</option>
                      {eventTypeOptions.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <CustomMapControl />
                <MapWrapper />
              </>
            )}
          </div>
          {/* <div className="w-[368px] lg:block hidden h-[623px] mt-[155px] overflow-hidden bg-white rounded-[10px] border-[0.5px] border-solid border-[#C2C2C2] p-6 gap-2">
            <h2 className="text-[#343434] font-[Montserrat] text-[16px] no-italic font-bold leading-6">
              Events
            </h2>
            {selectedEvents &&
            selectedEvents[0]?.city &&
            selectedEvents[0]?.state ? (
              <p className="text-[#343434] font-[Montserrat] text-[14px] no-italic font-bold leading-6">
                {`${selectedEvents[0].city}, ${selectedEvents[0].state}`}
              </p>
            ) : null}
            {selectedEvents?.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <div className="flex flex-col items-center gap-4">
                  <NoEventIcon />
                  <p className="text-[#808693] font-[Montserrat] text-[16px] no-italic font-normal leading-6">
                    No event selected
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-full w-full no-scrollbar overflow-y-scroll overflow-hidden">
                <div className="flex flex-col mt-2 gap-2">
                  {selectedEvents?.map((event, index) => (
                    <React.Fragment key={index}>
                      <div className="flex items-center gap-[13px]">
                        <img
                          src={event?.image}
                          alt={"Event Image"}
                          className="w-[154.5px] h-[159.667px] rounded-[10px] object-cover"
                        />

                        <div className="flex flex-col">
                          <p className="text-[#343434] font-[Montserrat] text-[18px] no-italic font-normal leading-[16.8px]">
                            {event?.eventName}
                          </p>
                          <p className="text-[#343434] font-[Montserrat] text-[14px] no-italic font-normal leading-[16.8px] mt-3">
                            {event.startDate && event.endDate
                              ? `${dayjs(event?.startDate).format(
                                  "MMM D YYYY"
                                )} - ${dayjs(event?.endDate).format(
                                  "MMM D YYYY"
                                )}`
                              : dayjs(event?.startDate).format("MMM D YYYY")}
                          </p>
                          <div
                            className="flex items-center gap-2 mt-4 cursor-pointer"
                            onClick={() => {
                              setPopupIndex(index);
                              handleBookNow(index);
                            }}
                          >
                            <p className="text-[#2B364B] font-[Montserrat] text-[14px] no-italic font-semibold underline">
                              Book now
                            </p>
                            <ArrowIcon />
                          </div>
                        </div>
                      </div>

                      <div className="w-[100%] h-[1px] bg-[#EAEBED]" />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div> */}
        </div>
      </div>

      {openModalIndex !== null && (
        <BookingModal
          isOpen={openModalIndex !== null}
          onClose={() => setOpenModalIndex(null)}
          event={
            (Array.isArray(selectedEvents) ? selectedEvents[openModalIndex] : null) ||
            eventsData?.[openModalIndex]
          }
          user={user}
        />
      )}
    </>
  );
};

export default Event;
